import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Fade,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";

import logo from "./logo.png";
import { loginAdmin } from "../api";

const accent = "#7209d4";
const fadedBg = "#F5F2FF";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleMagic = async () => {
    setError("");
    setLoading(true);

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email.");
      setLoading(false);
      return;
    }

    try {
      const res = await loginAdmin(email);

      if (!res.success) {
        setError(res.message || "Failed to send magic link.");
        setLoading(false);
        return;
      }

      setSuccess(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        sx={{
          width: 1000,
          minHeight: 520,
          borderRadius: 4,
          overflow: "hidden",
          display: "flex",
          boxShadow: "0 8px 40px #0001",
        }}
      >
        {/* LEFT: Login or Success */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 4,
            background: "#fff",
          }}
        >
          <Typography
            sx={{
              color: accent,
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: 22,
              mb: 5,
              mt: 2,
            }}
          >
            Readabled
          </Typography>

          {!success ? (
            <Fade in>
              <Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, mb: 3, color: "#181F2B" }}
                  gutterBottom
                >
                  Welcome Back
                </Typography>

                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  placeholder="m@example.com"
                  variant="outlined"
                  value={email}
                  size="medium"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  error={!!error}
                  helperText={error || " "}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineIcon sx={{ color: accent }} />
                      </InputAdornment>
                    ),
                    style: {
                      background: fadedBg,
                      borderRadius: 8,
                      fontSize: 18,
                      fontWeight: 600,
                    },
                  }}
                  sx={{ mb: 2 }}
                />

                <Typography
                  sx={{
                    fontSize: 14.5,
                    color: "#888",
                    mb: 3,
                  }}
                >
                  Tired of typing passwords?
                  <br />
                  Enter your email to get a magic link.
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    background: accent,
                    fontWeight: 700,
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontSize: 18,
                    py: 1.4,
                    boxShadow: "none",
                    mt: 1,
                    mb: 2,
                    "&:hover": { background: "#6c1ecb" },
                  }}
                  onClick={handleMagic}
                  disabled={loading}
                  startIcon={<ForwardToInboxIcon />}
                >
                  {loading ? "Sending..." : "Send Magic Link"}
                </Button>
              </Box>
            </Fade>
          ) : (
            <Fade in>
              <Box textAlign="center" pt={6}>
                <CheckCircleRoundedIcon
                  sx={{ color: "#15CA59", fontSize: 54, mb: 2 }}
                />
                <Typography variant="h5" sx={{ fontWeight: 700, my: 2 }}>
                  Magic link sent successfully
                </Typography>
                <Typography
                  sx={{
                    color: "#5B6274",
                    fontWeight: 500,
                    mb: 1,
                    fontSize: 18,
                  }}
                >
                  Check your email for a link to sign in.
                </Typography>
                <Typography sx={{ color: "#bbb", mt: 2 }}>
                  You can now close this page
                </Typography>
              </Box>
            </Fade>
          )}
        </Box>

        {/* RIGHT: Illustration */}
        <Box
          sx={{
            flex: 1,
            background: "#EBEAFE",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderLeft: "4px solid #fff",
            borderRadius: "0 12px 12px 0",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <img
            src={logo}
            alt="Magic R and owl"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPage;
